import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Zap,
  Info,
  Plus,
  Trash2,
  Users,
  Clock,
  Target,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import AdminLayout from "@/components/layout/AdminLayout";
import { supabase } from "@/integrations/supabase/client";

interface AutomationRule {
  id?: number;
  name: string;
  description: string;
  trigger_type: string;
  trigger_conditions: any;
  template_id: number;
  target_user_groups?: number[]; // Added missing field
  exclude_user_groups?: number[]; // Added missing field
  delay_duration?: string; // Added missing field
  frequency_cap?: string; // Added missing field
  send_window_start?: string; // Added missing field
  send_window_end?: string; // Added missing field
  timezone_aware?: boolean; // Added missing field
  ab_test_enabled?: boolean; // Added missing field
  control_group_percentage?: number; // Added missing field
  is_active: boolean;
  created_by?: number;
}

interface NotificationTemplate {
  id: number;
  name: string;
  category: string;
}

interface UserGroup {
  id: number;
  name: string;
  description: string;
  member_count: number;
}

const TRIGGER_TYPES = [
  { value: "event", label: "Event-based" },
  { value: "schedule", label: "Schedule-based" },
  { value: "user_behavior", label: "User Behavior" },
  { value: "subscription_change", label: "Subscription Change" },
];

const EVENT_CONDITIONS = [
  { value: "user_signup", label: "User signs up" },
  { value: "subscription_start", label: "Subscription starts" },
  { value: "subscription_end", label: "Subscription ends" },
  { value: "book_completed", label: "Book completed" },
  { value: "reading_streak", label: "Reading streak milestone" },
  { value: "inactive_days", label: "User inactive for X days" },
];

const SCHEDULE_CONDITIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "custom", label: "Custom Schedule" },
];

const BEHAVIOR_CONDITIONS = [
  { value: "reading_time", label: "Reading time threshold" },
  { value: "books_read", label: "Number of books read" },
  { value: "login_frequency", label: "Login frequency" },
  { value: "feature_usage", label: "Feature usage" },
];

export default function NotificationAutomationForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();

  const [formData, setFormData] = useState<AutomationRule>({
    name: "",
    description: "",
    trigger_type: "event",
    trigger_conditions: {},
    template_id: 0,
    target_user_groups: [],
    exclude_user_groups: [],
    delay_duration: "",
    frequency_cap: "",
    send_window_start: "",
    send_window_end: "",
    timezone_aware: true,
    ab_test_enabled: false,
    control_group_percentage: 0,
    is_active: true,
  });

  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [conditionFields, setConditionFields] = useState<any[]>([]);

  const isEditing = !!id;

  useEffect(() => {
    fetchTemplates();
    fetchUserGroups();
    if (isEditing) {
      fetchAutomationRule();
    }
  }, [id]);

  useEffect(() => {
    updateConditionFields();
  }, [formData.trigger_type]);

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from("notification_templates" as any)
        .select("id, name, category")
        .eq("is_active", true)
        .order("name");

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const fetchUserGroups = async () => {
    try {
      const { data, error } = await supabase
        .from("user_groups" as any)
        .select("id, name, description, member_count")
        .eq("is_active", true)
        .order("name");

      if (error) throw error;
      setUserGroups(data || []);
    } catch (error) {
      console.error("Error fetching user groups:", error);
    }
  };

  const fetchAutomationRule = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("notification_automation_rules" as any)
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      if (data) {
        console.log("Fetched automation rule data:", data);
        setFormData({
          ...data,
          trigger_conditions: data.trigger_conditions || {},
          target_user_groups: data.target_user_groups || [],
          exclude_user_groups: data.exclude_user_groups || [],
          delay_duration: data.delay_duration || "",
          frequency_cap: data.frequency_cap || "",
          send_window_start: data.send_window_start || "",
          send_window_end: data.send_window_end || "",
          timezone_aware: data.timezone_aware ?? true,
          ab_test_enabled: data.ab_test_enabled ?? false,
          control_group_percentage: data.control_group_percentage || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching automation rule:", error);
      toast({
        title: "Error",
        description: "Failed to fetch automation rule details.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateConditionFields = () => {
    let fields = [];

    switch (formData.trigger_type) {
      case "event":
        fields = EVENT_CONDITIONS;
        break;
      case "schedule":
        fields = SCHEDULE_CONDITIONS;
        break;
      case "user_behavior":
        fields = BEHAVIOR_CONDITIONS;
        break;
      case "subscription_change":
        fields = [
          { value: "status_change", label: "Status changes to" },
          { value: "plan_change", label: "Plan changes to" },
          { value: "trial_ending", label: "Trial ending in X days" },
        ];
        break;
      default:
        fields = [];
    }

    setConditionFields(fields);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({
        title: "Error",
        description: "Rule name is required.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.template_id) {
      toast({
        title: "Error",
        description: "Please select a notification template.",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);

      const payload = {
        name: formData.name,
        description: formData.description,
        trigger_type: formData.trigger_type,
        trigger_conditions: formData.trigger_conditions,
        template_id: formData.template_id,
        target_user_groups: formData.target_user_groups,
        exclude_user_groups: formData.exclude_user_groups,
        delay_duration: formData.delay_duration || null,
        frequency_cap: formData.frequency_cap || null,
        send_window_start: formData.send_window_start || null,
        send_window_end: formData.send_window_end || null,
        timezone_aware: formData.timezone_aware,
        ab_test_enabled: formData.ab_test_enabled,
        control_group_percentage: formData.control_group_percentage,
        is_active: formData.is_active,
      };

      console.log("Submitting automation rule payload:", payload);

      if (isEditing) {
        const { error } = await supabase
          .from("notification_automation_rules" as any)
          .update(payload)
          .eq("id", id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("notification_automation_rules" as any)
          .insert(payload);

        if (error) throw error;
      }

      toast({
        title: "Success",
        description: `Automation rule ${
          isEditing ? "updated" : "created"
        } successfully.`,
      });

      navigate("/admin/notifications/automation");
    } catch (error) {
      console.error("Error saving automation rule:", error);
      toast({
        title: "Error",
        description: `Failed to ${
          isEditing ? "update" : "create"
        } automation rule: ${error}`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateCondition = (key: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      trigger_conditions: {
        ...prev.trigger_conditions,
        [key]: value,
      },
    }));
  };

  const removeCondition = (key: string) => {
    const newConditions = { ...formData.trigger_conditions };
    delete newConditions[key];
    setFormData((prev) => ({
      ...prev,
      trigger_conditions: newConditions,
    }));
  };

  const addCustomCondition = () => {
    const key = `custom_${Date.now()}`;
    updateCondition(key, "");
  };

  if (loading && isEditing) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  const backButton = (
    <Button
      variant="ghost"
      onClick={() => navigate("/admin/notifications/automation")}
    >
      <ArrowLeft className="h-4 w-4 mr-2" />
      Back to Automation Rules
    </Button>
  );

  return (
    <AdminLayout
      title={isEditing ? "Edit Automation Rule" : "Create Automation Rule"}
      actionButton={backButton}
      mainClassName="flex-1 overflow-y-auto p-6 bg-background max-w-4xl mx-auto"
    >
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>
              Configure the basic details of your automation rule
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Rule Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="e.g., Welcome New Users, Trial Ending Reminder"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Describe what this automation rule does..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="template_id">Notification Template *</Label>
              <Select
                value={
                  formData.template_id ? String(formData.template_id) : "none"
                }
                onValueChange={(value) => {
                  if (value === "none") {
                    setFormData((prev) => ({ ...prev, template_id: 0 }));
                  } else {
                    setFormData((prev) => ({
                      ...prev,
                      template_id: parseInt(value),
                    }));
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a template" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select a template</SelectItem>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={String(template.id)}>
                      <div className="flex items-center gap-2">
                        <span>{template.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {template.category}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="is_active">Active</Label>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Active rules will automatically trigger notifications
                </p>
              </div>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, is_active: checked }))
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Trigger Configuration */}
        <Card>
          <CardHeader>
            <CardTitle>Trigger Configuration</CardTitle>
            <CardDescription>
              Define when this automation rule should trigger
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="trigger_type">Trigger Type *</Label>
              <Select
                value={formData.trigger_type}
                onValueChange={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    trigger_type: value,
                    trigger_conditions: {}, // Reset conditions when type changes
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select trigger type" />
                </SelectTrigger>
                <SelectContent>
                  {TRIGGER_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Configure the conditions that will trigger this automation rule.
                All conditions must be met for the rule to activate.
              </AlertDescription>
            </Alert>

            {/* Condition Fields */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Trigger Conditions</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addCustomCondition}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Custom Condition
                </Button>
              </div>

              {conditionFields.map((field) => (
                <div key={field.value} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>{field.label}</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCondition(field.value)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  {field.value === "custom" ||
                  field.value.startsWith("custom_") ? (
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Condition key"
                        value={
                          Object.keys(formData.trigger_conditions).find(
                            (k) => k === field.value
                          ) || ""
                        }
                        onChange={(e) => {
                          const oldValue =
                            formData.trigger_conditions[field.value];
                          removeCondition(field.value);
                          updateCondition(e.target.value, oldValue || "");
                        }}
                      />
                      <Input
                        placeholder="Condition value"
                        value={formData.trigger_conditions[field.value] || ""}
                        onChange={(e) =>
                          updateCondition(field.value, e.target.value)
                        }
                      />
                    </div>
                  ) : (
                    <Input
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                      value={formData.trigger_conditions[field.value] || ""}
                      onChange={(e) =>
                        updateCondition(field.value, e.target.value)
                      }
                    />
                  )}
                </div>
              ))}

              {/* Custom Conditions */}
              {Object.entries(formData.trigger_conditions)
                .filter(([key]) => key.startsWith("custom_"))
                .map(([key, value]) => (
                  <div key={key} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Custom Condition</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCondition(key)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Condition key"
                        value={key.replace("custom_", "")}
                        onChange={(e) => {
                          removeCondition(key);
                          updateCondition(`custom_${e.target.value}`, value);
                        }}
                      />
                      <Input
                        placeholder="Condition value"
                        value={String(value)}
                        onChange={(e) => updateCondition(key, e.target.value)}
                      />
                    </div>
                  </div>
                ))}
            </div>

            {/* Current Conditions Summary */}
            {Object.keys(formData.trigger_conditions).length > 0 && (
              <div className="pt-4 border-t">
                <Label className="text-sm font-medium">
                  Current Conditions:
                </Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {Object.entries(formData.trigger_conditions).map(
                    ([key, value]) => (
                      <Badge
                        key={key}
                        variant="secondary"
                        className="flex items-center gap-1"
                      >
                        {key}: {String(value)}
                        <button
                          type="button"
                          onClick={() => removeCondition(key)}
                          className="ml-1 hover:text-red-600"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </Badge>
                    )
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-between pt-6 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/admin/notifications/automation")}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            <Save className="h-4 w-4 mr-2" />
            {loading ? "Saving..." : isEditing ? "Update Rule" : "Create Rule"}
          </Button>
        </div>
      </form>
    </AdminLayout>
  );
}
